import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  doc, 
  deleteDoc 
} from 'firebase/firestore';
import { db } from '../../firebase/config';

// Async thunks
export const fetchAds = createAsyncThunk(
  'ads/fetchAds',
  async (_, { rejectWithValue }) => {
    try {
      const adsRef = collection(db, 'ads');
      const snapshot = await getDocs(adsRef);
      const ads = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return ads;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createAd = createAsyncThunk(
  'ads/createAd',
  async (adData, { rejectWithValue }) => {
    try {
      const adsRef = collection(db, 'ads');
      const docRef = await addDoc(adsRef, {
        ...adData,
        createdAt: new Date(),
        impressions: 0,
        clicks: 0
      });
      return {
        id: docRef.id,
        ...adData,
        createdAt: new Date(),
        impressions: 0,
        clicks: 0
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAd = createAsyncThunk(
  'ads/updateAd',
  async (adData, { rejectWithValue }) => {
    try {
      const { id, ...updates } = adData;
      const adRef = doc(db, 'ads', id);
      await updateDoc(adRef, {
        ...updates,
        updatedAt: new Date()
      });
      return adData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAd = createAsyncThunk(
  'ads/deleteAd',
  async (adId, { rejectWithValue }) => {
    try {
      const adRef = doc(db, 'ads', adId);
      await deleteDoc(adRef);
      return adId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const adsSlice = createSlice({
  name: 'ads',
  initialState: {
    items: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null
  },
  reducers: {
    resetAdsStatus: (state) => {
      state.status = 'idle';
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchAds
      .addCase(fetchAds.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAds.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchAds.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Handle createAd
      .addCase(createAd.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createAd.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items.push(action.payload);
      })
      .addCase(createAd.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Handle updateAd
      .addCase(updateAd.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateAd.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.items.findIndex(ad => ad.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateAd.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Handle deleteAd
      .addCase(deleteAd.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteAd.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = state.items.filter(ad => ad.id !== action.payload);
      })
      .addCase(deleteAd.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  }
});

export const { resetAdsStatus } = adsSlice.actions;
export default adsSlice.reducer;
